import {
    CountryBankType,
    PayoutType,
    AnnualIncomeRangeInThousandEuro,
    BillingInformationType,
} from "@daytrip/legacy-enums";
import { Exclude, Expose, Transform, Type } from "class-transformer";
import {
    IsArray,
    IsBoolean,
    IsDate,
    IsDefined,
    IsEmail,
    IsEnum,
    IsInt,
    IsNumber,
    IsOptional,
    IsString,
    IsUUID,
    Validate,
    ValidateIf,
    ValidateNested,
} from "class-validator";
import { v4 as uuid } from "uuid";

import { IsABAValidator } from "../validators/IsABAValidator";
import { IsBICValidator } from "../validators/IsBICValidator";
import { IsCompanyNumber } from "../validators/IsCompanyNumber";
import { IsIbanValidator } from "../validators/IsIbanValidator";

import { Address } from "./Address";
import { Document } from "./Document";
import { UBODeclaration } from "./UBODeclaration";
import { VatValidation } from "./VatValidation";

@Exclude()
export class BillingInformation {
    @IsUUID("4")
    @IsDefined()
    @Expose()
    public _id: string = uuid();

    @IsInt()
    @IsDefined()
    @Expose()
    public type: BillingInformationType;

    @IsDefined()
    @IsEnum(PayoutType)
    @Expose()
    public payoutType: PayoutType;

    @IsBoolean()
    @IsDefined()
    @Expose()
    public isActive: boolean = false;

    @IsDate()
    @IsDefined()
    @Expose()
    @Type(() => Date)
    @Transform((value) => (!value ? undefined : new Date(value)))
    @ValidateIf((bi: BillingInformation) => bi.isActive)
    public activatedAt: Date;

    @IsBoolean()
    @Expose()
    public isInvoiceInformation: boolean = false;

    @IsEmail()
    @IsDefined()
    @Expose()
    public email: string;

    @IsString()
    @IsDefined()
    @ValidateIf(
        (bi: BillingInformation) => (bi.isLegal() || bi.isSoletrader()) && (bi.isActive || bi.isInvoiceInformation),
    )
    @Expose()
    public bussinessName?: string;

    @IsString()
    @IsDefined()
    @Expose()
    @ValidateIf(
        (bi: BillingInformation) =>
            (bi.isMangopayInformationRequired() || bi.isNatural()) && (bi.isActive || bi.isInvoiceInformation),
    )
    public firstName: string;

    @IsString()
    @IsDefined()
    @Expose()
    @ValidateIf(
        (bi: BillingInformation) =>
            (bi.isMangopayInformationRequired() || bi.isNatural()) && (bi.isActive || bi.isInvoiceInformation),
    )
    public lastName: string;

    @IsDate()
    @IsDefined()
    @Expose()
    @Type(() => Date)
    @Transform((value) => (!value ? undefined : new Date(value)))
    @ValidateIf((bi: BillingInformation) => bi.isMangopayInformationRequired() && bi.isActive)
    public birthdayAt: Date;

    @IsDefined()
    @Type(() => Address)
    @Expose()
    @ValidateIf(
        (bi: BillingInformation) =>
            (bi.isMangopayInformationRequired() || bi.isNatural()) && (bi.isActive || bi.isInvoiceInformation),
    )
    @ValidateNested()
    public address: Address = new Address();

    @IsDefined()
    @Type(() => Address)
    @ValidateIf(
        (bi: BillingInformation) => (bi.isLegal() || bi.isSoletrader()) && (bi.isActive || bi.isInvoiceInformation),
    )
    @Expose()
    @ValidateNested()
    public headquartersAddress?: Address;

    @IsDefined()
    @Expose()
    @ValidateIf((bi: BillingInformation) => bi.isMangopayInformationRequired() && bi.isActive)
    public bankAccountCountryId: string;

    @Expose()
    @IsOptional()
    @Type(() => VatValidation)
    @ValidateNested()
    public vatValidation?: VatValidation;

    // KYC

    @IsUUID("4")
    @IsDefined()
    @Expose()
    @ValidateIf((bi: BillingInformation) => bi.isMangopayInformationRequired() && bi.isActive)
    public nationalityCountryId: string;

    @IsUUID("4")
    @IsDefined()
    @Expose()
    @ValidateIf((bi: BillingInformation) => (bi.isMangopayInformationRequired() || bi.isNatural()) && bi.isActive)
    public residenceCountryId: string;

    @IsUUID("4")
    @IsOptional()
    @Expose()
    @ValidateIf((bi: BillingInformation) => bi.isMangopayInformationRequired() && bi.isActive)
    public taxResidenceCountryId: string;

    @IsString()
    @IsDefined()
    @ValidateIf((bi: BillingInformation) => bi.isNatural() && bi.isMangopayInformationRequired() && bi.isActive)
    @Expose()
    public occupation?: string;

    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => Document)
    @ValidateIf((bi: BillingInformation) => bi.isMangopayInformationRequired() && bi.isActive)
    public documents: Document[] = [];

    @IsNumber()
    @IsDefined()
    @ValidateIf((bi: BillingInformation) => bi.isNatural() && bi.isMangopayInformationRequired() && bi.isActive)
    @Expose()
    public annualIncomeRangeInEuro?: AnnualIncomeRangeInThousandEuro;

    // @IsDate()
    @Expose()
    @Transform((value) => (!value ? null : new Date(value)))
    public deletedAt: Date | null = null;

    // additional fields required by accountant

    @IsString()
    @ValidateIf(
        (bi: BillingInformation) =>
            (bi.isLegal() || bi.isSoletrader()) && bi.isActive && bi.isMangopayInformationRequired(),
    )
    @IsDefined()
    @Validate(IsCompanyNumber)
    @Transform((value?: string) => (value ? value.replace(/\s/g, "") : undefined))
    @Expose()
    public companyIdNumber?: string;

    @IsString()
    @ValidateIf(
        (bi: BillingInformation) =>
            bi.isLegal() && !!bi.taxIdentificationNumber && bi.isMangopayInformationRequired() && bi.isActive,
    )
    @Expose()
    public taxIdentificationNumber?: string;

    @Expose()
    @ValidateIf(
        (bi: BillingInformation) => bi.isMangopayInformationRequired() && (bi.isActive || bi.isInvoiceInformation),
    )
    public vatNumber?: string;

    @IsString()
    @ValidateIf(
        (bi: BillingInformation) =>
            bi.isNatural() && !!bi.identifiedPersonNumber && bi.isMangopayInformationRequired() && bi.isActive,
    )
    @Expose()
    public identifiedPersonNumber?: string;

    @Expose()
    @Type(() => UBODeclaration)
    @ValidateIf((bi: BillingInformation) => bi.isMangopayInformationRequired() && bi.isActive)
    public uboDeclaration?: UBODeclaration = new UBODeclaration();

    @Validate(IsIbanValidator)
    @IsDefined()
    @ValidateIf(
        (bi: BillingInformation) =>
            bi.bankType === CountryBankType.IBAN && bi.isMangopayInformationRequired() && bi.isActive,
    )
    @Expose()
    public iban?: string;

    @Validate(IsBICValidator)
    @IsDefined()
    @ValidateIf(
        (bi: BillingInformation) =>
            bi.bankType === CountryBankType.BIC && bi.isMangopayInformationRequired() && bi.isActive,
    )
    @Expose()
    public bic?: string;

    @Validate(IsABAValidator)
    @IsDefined()
    @ValidateIf(
        (bi: BillingInformation) =>
            bi.bankType === CountryBankType.US && bi.isMangopayInformationRequired() && bi.isActive,
    )
    @Expose()
    public aba?: string;

    @IsDefined()
    @ValidateIf(
        (bi: BillingInformation) =>
            (bi.bankType === CountryBankType.BIC || bi.bankType === CountryBankType.US) &&
            bi.isMangopayInformationRequired() &&
            bi.isActive,
    )
    @Expose()
    public accountNumber?: string;

    @IsEnum(CountryBankType)
    @IsDefined()
    @Expose()
    @ValidateIf((bi: BillingInformation) => bi.isMangopayInformationRequired() && bi.isActive)
    public bankType: CountryBankType;

    public isLegal(): this is this & LegalBillingInformation {
        return this.type === BillingInformationType.Legal;
    }

    public isNatural(): this is this & NaturalBillingInformation {
        return this.type === BillingInformationType.Natural;
    }

    public isSoletrader(): this is this & LegalBillingInformation {
        return this.type === BillingInformationType.Soletrader;
    }

    public isMangopayInformationRequired() {
        return this.payoutType === PayoutType.Mangopay;
    }

    public get invoiceCountryId(): string {
        if (this.type === BillingInformationType.Natural) {
            return this.residenceCountryId;
        }

        return (this.headquartersAddress as Address).countryId;
    }
}

interface LegalBillingInformation {
    bussinessName: string;
    headquartersAddress: Address;
}

interface NaturalBillingInformation {
    occupation: string;
    annualIncomeRangeInEuro: AnnualIncomeRangeInThousandEuro;
}
